import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Empty, Row, Segmented, Space, Spin, Statistic } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import diskAdminService from '../../services/diskAdminService';
import { fmtBytes, toastErr } from './common';

// 已知统计项的中文名；其余数值字段原样展示（键名）
const LABELS = {
    accounts: '账号', accounts_total: '账号', personal_accounts: '个人账号', member_accounts: '组织成员',
    orgs: '组织', orgs_total: '组织', active_orgs: '正常组织',
    files: '文件', files_total: '文件', folders: '文件夹',
    storage_used: '已用存储', storage_bytes: '已用存储', used_bytes: '已用存储', objects_bytes: '对象存储',
    shares: '分享', shares_total: '分享', active_shares: '有效分享',
    links: '直链', links_total: '直链', active_links: '启用直链',
    groups: '群组', notes: '笔记',
    traffic_today: '今日流量', traffic_today_bytes: '今日流量', traffic_month: '本月流量', traffic_month_bytes: '本月流量',
    uploads_pending: '上传中', pending_uploads: '上传中',
    hashes_pending: '待校验', hashes_failed: '校验失败',
};
const isBytes = (k) => /bytes|storage|traffic/.test(k);

const flatten = (obj, prefix = '') => {
    const out = [];
    Object.entries(obj || {}).forEach(([k, v]) => {
        const key = prefix ? `${prefix}_${k}` : k;
        if (v !== null && typeof v === 'object' && !Array.isArray(v)) out.push(...flatten(v, key));
        else if (typeof v === 'number' || (typeof v === 'string' && v !== '' && !Number.isNaN(Number(v)))) out.push([key, Number(v)]);
    });
    return out;
};

const DiskOverview = () => {
    const [stats, setStats] = useState(null);
    const [traffic, setTraffic] = useState([]);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, tr] = await Promise.all([diskAdminService.stats(), diskAdminService.traffic(days)]);
            setStats(s?.data || null);
            const d = tr?.data;
            setTraffic(Array.isArray(d) ? d : (d?.items || d?.series || d?.days || []));
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { load(); }, [load]);

    const items = useMemo(() => flatten(stats), [stats]);

    const chart = useMemo(() => {
        const rows = traffic.map((r) => ({
            date: String(r.date || r.day || r.d || '').slice(0, 10),
            bytes: Number(r.bytes ?? r.traffic_bytes ?? r.total_bytes ?? 0),
            hits: Number(r.hits ?? r.requests ?? 0),
        }));
        return {
            tooltip: {
                trigger: 'axis',
                formatter: (ps) => `${ps[0]?.axisValue}<br/>${ps.map((p) => `${p.marker}${p.seriesName}: ${p.seriesIndex === 0 ? fmtBytes(p.value) : Number(p.value).toLocaleString()}`).join('<br/>')}`,
            },
            legend: { data: ['流量', '请求'], top: 0 },
            grid: { left: 70, right: 50, top: 36, bottom: 30 },
            xAxis: { type: 'category', data: rows.map((r) => r.date) },
            yAxis: [
                { type: 'value', axisLabel: { formatter: (v) => fmtBytes(v) } },
                { type: 'value', splitLine: { show: false } },
            ],
            series: [
                { name: '流量', type: 'bar', data: rows.map((r) => r.bytes), itemStyle: { color: '#1677ff' } },
                { name: '请求', type: 'line', yAxisIndex: 1, smooth: true, data: rows.map((r) => r.hits), itemStyle: { color: '#fa8c16' } },
            ],
        };
    }, [traffic]);

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card title="云盘总览" extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} />}>
                <Spin spinning={loading && !stats}>
                    {items.length === 0
                        ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        : (
                            <Row gutter={[16, 16]}>
                                {items.map(([k, v]) => (
                                    <Col key={k} xs={12} sm={8} md={6} lg={4}>
                                        <Statistic title={LABELS[k] || k} value={isBytes(k) ? fmtBytes(v) : v} />
                                    </Col>
                                ))}
                            </Row>
                        )}
                </Spin>
            </Card>
            <Card title="直链 / 分享下载流量"
                extra={<Segmented value={days} onChange={setDays} options={[{ value: 7, label: '7 天' }, { value: 30, label: '30 天' }, { value: 90, label: '90 天' }]} />}>
                {traffic.length === 0
                    ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无流量" />
                    : <ReactECharts option={chart} style={{ height: 320 }} notMerge />}
            </Card>
        </Space>
    );
};

export default DiskOverview;
